import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { api, formatApiError } from "../lib/api";
import { Check } from "lucide-react";

export default function RaterEvaluation() {
  const { token } = useParams();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [answers, setAnswers] = useState({});
  const [strengths, setStrengths] = useState("");
  const [weaknesses, setWeaknesses] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/rater/${token}`).then(({ data }) => {
      setData(data);
      if (data.rater.status === "completed") setDone(true);
    }).catch((e) => setErr(formatApiError(e)));
  }, [token]);

  if (err && !data) return <div className="min-h-screen flex items-center justify-center p-8 text-center text-ink/70"><div><h2 className="font-serif text-3xl mb-4">Lien invalide</h2><p>{err}</p></div></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-ink/50 font-mono text-xs uppercase tracking-[0.25em]">Chargement…</div>;
  if (done) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-ivory">
      <div className="max-w-md text-center">
        <Check size={48} className="text-brand mx-auto mb-6" />
        <h1 className="font-serif text-4xl font-light text-navy mb-4">{lang === "fr" ? "Merci pour votre évaluation." : "Thank you for your evaluation."}</h1>
        <p className="text-ink/65">{lang === "fr" ? "Vos retours, strictement confidentiels, seront partagés de manière anonymisée." : "Your feedback, strictly confidential, will be shared anonymously."}</p>
      </div>
    </div>
  );

  const tpl = data.template;
  const scale = tpl.scale;
  const labels = lang === "fr" ? scale.labels_fr : scale.labels_en;

  const submit = async () => {
    // Identify missing question line numbers (1-indexed). NE ("Non évaluable") is a valid answer.
    const missingNumbers = [];
    tpl.questions.forEach((q, idx) => {
      const a = answers[q.id];
      if (a === undefined || a === null || a === "") missingNumbers.push(idx + 1);
    });
    if (missingNumbers.length) {
      const numbersText = missingNumbers.join(", ");
      window.alert(
        lang === "fr"
          ? `Questionnaire incomplet — merci de compléter les lignes suivantes avant de soumettre :\n\n${numbersText}\n\n(Vous pouvez sélectionner « NE » si vous n'êtes pas en mesure de juger une dimension.)`
          : `Incomplete questionnaire — please fill in the following lines before submitting:\n\n${numbersText}\n\n(You can select "NE" if you cannot judge a dimension.)`
      );
      setErr(lang === "fr"
        ? `Lignes à compléter : ${numbersText}`
        : `Lines to fill in: ${numbersText}`);
      const firstId = tpl.questions[missingNumbers[0] - 1].id;
      const el = document.getElementById(`rater-q-${firstId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setErr("");
    setSubmitting(true);
    try {
      await api.post(`/rater/${token}/submit`, { answers, strengths, weaknesses });
      setDone(true);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  // Group questions by category if the template uses dimensions
  const dims = tpl.dimensions || {};
  const groupedByDim = {};
  if (tpl.multi_dimension) {
    for (const q of tpl.questions) {
      const d = q.dim || "_";
      (groupedByDim[d] = groupedByDim[d] || []).push(q);
    }
  }

  const renderRow = (q, idx) => (
    <div key={q.id} id={`rater-q-${q.id}`} className={`px-3 md:px-4 py-2 ${idx % 2 === 1 ? "bg-ivory/40" : "bg-white"} ${(answers[q.id] === undefined || answers[q.id] === null || answers[q.id] === "") && err && err.includes("Lignes à compléter") ? "ring-2 ring-destructive/40" : ""}`} data-testid={`rater-q-${q.id}`}>
      <div className="flex items-baseline gap-2.5 mb-1.5">
        <span className="font-mono text-[10px] text-brand font-semibold w-5 flex-none">{String(idx + 1).padStart(2, "0")}</span>
        <p className="flex-1 text-[13px] text-ink font-light leading-snug">{lang === "fr" ? q.fr : q.en}</p>
      </div>
      <div className="grid gap-px bg-black/5 ml-7" style={{ gridTemplateColumns: `${tpl.allow_skip ? "36px " : ""}repeat(${scale.max - scale.min + 1}, 1fr)` }}>
        {tpl.allow_skip && (
          <button
            data-testid={`rater-q-${q.id}-NE`}
            onClick={() => setAnswers({ ...answers, [q.id]: "NE" })}
            className={`p-1 text-center transition-all border ${answers[q.id] === "NE" ? "bg-navy text-white border-navy" : "bg-white border-transparent text-ink/55 hover:bg-ivory hover:text-navy hover:border-navy/30"}`}
            title="Non évaluable"
          >
            <div className="font-mono text-[10px] font-semibold">NE</div>
          </button>
        )}
        {Array.from({ length: scale.max - scale.min + 1 }, (_, i) => {
          const v = scale.min + i;
          const sel = answers[q.id] === v;
          const tint = ["bg-rose-50", "bg-amber-50", "bg-yellow-50", "bg-lime-50", "bg-emerald-50", "bg-emerald-100"][Math.min(i, 5)];
          return (
            <button
              key={v}
              data-testid={`rater-q-${q.id}-${v}`}
              onClick={() => setAnswers({ ...answers, [q.id]: v })}
              className={`p-1 text-center transition-all border ${sel ? "bg-brand text-white border-brand scale-105 shadow-sm" : `${tint} border-transparent hover:border-brand/40 text-ink/80`}`}
            >
              <div className="num-display text-sm leading-none">{v}</div>
              {labels && labels[i] && <div className="text-[8px] mt-0.5 uppercase tracking-wide leading-tight truncate">{labels[i]}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-ivory py-16 px-6 md:px-10">
      <div className="max-w-3xl mx-auto">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-5">— {lang === "fr" ? "Évaluation 360°" : "360° Evaluation"}</div>
        <h1 className="font-serif text-4xl md:text-5xl font-light text-navy leading-tight">{lang === "fr" ? `À propos de ${data.coachee_name}` : `About ${data.coachee_name}`}</h1>
        <p className="mt-3 text-ink/65 text-base">{lang === "fr" ? "Votre regard est précieux. Évaluation strictement confidentielle, restitution anonymisée." : "Your perspective matters. Strictly confidential, anonymised aggregation."}</p>
        <p className="mt-4 text-sm font-medium text-brand">{lang === "fr" ? "⚠ Merci de répondre à toutes les lignes du questionnaire (utilisez NE si vous n'êtes pas en mesure de juger une dimension)." : "⚠ Please answer every line of the questionnaire (use NE if you cannot judge a dimension)."}</p>
        {tpl.allow_skip && (
          <p className="mt-2 text-xs text-ink/55 italic">{lang === "fr" ? "Pour chaque compétence : 1 = Pas du tout, 6 = Expertise. Choisissez NE (Non évaluable) si vous n'êtes pas en mesure de juger." : "For each competency: 1 = Not at all, 6 = Expertise. Pick NE (Not evaluable) if you cannot judge."}</p>
        )}
        <div className="text-xs text-ink/50 mt-4 font-mono uppercase tracking-wide">{lang === "fr" ? "Évaluateur" : "Rater"} : {data.rater.name} · {data.category_labels[data.rater.category][lang]}</div>

        {tpl.multi_dimension ? (
          <div className="mt-10 space-y-8">
            {Object.entries(dims).map(([dimId, dimMeta]) => {
              const qs = groupedByDim[dimId] || [];
              if (qs.length === 0) return null;
              return (
                <div key={dimId} data-testid={`rater-section-${dimId}`}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand mb-3">— {lang === "fr" ? dimMeta.fr : dimMeta.en}</div>
                  <div className="divide-y divide-black/10 bg-white border border-black/10">
                    {qs.map((q) => renderRow(q, tpl.questions.indexOf(q)))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-10 divide-y divide-black/10 bg-white border border-black/10">
            {tpl.questions.map((q, i) => renderRow(q, i))}
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-black/10 p-6">
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-brand mb-3">— {lang === "fr" ? "Points forts" : "Strengths"}</label>
            <textarea data-testid="rater-strengths" rows={5} value={strengths} onChange={(e) => setStrengths(e.target.value)} placeholder={lang === "fr" ? "Ce que cette personne fait particulièrement bien…" : "What this person does particularly well…"} className="w-full bg-transparent focus:outline-none text-sm resize-none"></textarea>
          </div>
          <div className="bg-white border border-black/10 p-6">
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-brand mb-3">— {lang === "fr" ? "Points d'amélioration" : "Points to improve"}</label>
            <textarea data-testid="rater-weaknesses" rows={5} value={weaknesses} onChange={(e) => setWeaknesses(e.target.value)} placeholder={lang === "fr" ? "Ce qui pourrait être renforcé…" : "What could be strengthened…"} className="w-full bg-transparent focus:outline-none text-sm resize-none"></textarea>
          </div>
        </div>

        {err && <div className="mt-6 text-sm text-destructive">{err}</div>}
        <button data-testid="rater-submit" onClick={submit} disabled={submitting} className="mt-10 bg-brand hover:bg-brandDark disabled:opacity-60 text-white px-8 py-5 text-sm uppercase tracking-[0.16em] font-mono">
          {(() => {
            if (submitting) return "...";
            return lang === "fr" ? "Soumettre l'évaluation" : "Submit evaluation";
          })()}
        </button>
      </div>
    </div>
  );
}
